'use client';

import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { usePostHog } from '@posthog/react';

import { queryFunctions } from '@hub/common/src/query-functions';
import { type Entry, FolderLayout } from '@hub/common/src/types/cit-public-api';

import SearchingAnimation from '@hub/common-components/src/SearchingAnimation';
import EntryCard from '@hub/common-components/src/EntryCard';
import useCustomTooltip from '@hub/common-components/src/custom-tooltip';
import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import { useCookieConsentStatus } from '@hub/common-components/src/CookieConsentStatusProvider';
import { trackSearch } from '@hub/common-components/src/PostHogAnalyticsProvider';
import { useCitThemeClient } from './CitThemeProvider';

// keep in sync with ScrolledEntriesDisplay so rows match the virtualized list
const CARD_PADDING = 5;

export default function AccountSearchResults({
  searchQuery,
  accountId
}: {
  searchQuery: string;
  accountId: string;
}) {
  const citOrigin = useCitOriginClient();

  const posthog = usePostHog();

  const { consentLoaded } = useCookieConsentStatus();

  const {
    data: searchResults,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['account-search-results', { searchQuery, accountId, citOrigin }],
    queryFn: queryFunctions.getAccountSearchResults
  });

  const { data: accountDetails, isLoading: isAccountDetailsLoading } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  const { accountTheme } = useCitThemeClient();

  const { customTooltip, handleHideToolTip, handleShowToolTip } = useCustomTooltip();

  const mediaAddornment = accountTheme.mediaAdornment();

  const isGrid = accountTheme.folderLayout() === FolderLayout.GRID;

  const thumbnailSize = accountTheme.thumbnailSize();

  // scroll window back to top when search query changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchQuery]);

  // fire search analytics once per query after results settle
  useEffect(() => {
    // PostHogSyncProperties mounts once consent loads and sets the account group before this runs
    if (!searchQuery || !consentLoaded) {
      return;
    }

    if (isLoading || isFetching) {
      return;
    }

    trackSearch(posthog, { accountId }, searchQuery, !!searchResults?.entries?.length);
  }, [searchQuery, accountId, isLoading, isFetching, searchResults, posthog, consentLoaded]);

  if (isLoading || isFetching || isAccountDetailsLoading || !accountDetails) {
    return <SearchingAnimation />;
  }

  const bodyFontColor = accountTheme.bodyFontColor();

  const bodyFont = accountTheme.bodyFont();

  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  return (
    <div className='py-6' style={{ fontFamily: bodyFont }}>
      <h2
        className='text-2xl font-bold mb-6'
        style={{
          color: bodyFontColor,
          fontFamily: bodyFont,
          lineHeight,
          letterSpacing
        }}
      >
        Search Results for &quot;{searchQuery}&quot;
      </h2>
      {customTooltip}

      {!searchResults?.entries || searchResults.entries.length === 0 ? (
        <p
          className='text-lg'
          style={{
            color: bodyFontColor,
            fontFamily: bodyFont,
            lineHeight,
            letterSpacing
          }}
        >
          No results found.
        </p>
      ) : (
        <div
          className={
            isGrid
              ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6'
              : 'flex flex-col gap-6'
          }
        >
          {searchResults.entries.map((result: Entry) => (
            <EntryCard
              key={result.id}
              entry={result}
              searchQuery={searchQuery}
              handleHideToolTip={handleHideToolTip}
              handleShowToolTip={handleShowToolTip}
              mediaAddornment={mediaAddornment}
              imageSize={isGrid ? undefined : thumbnailSize}
              cardPadding={isGrid ? undefined : CARD_PADDING}
              entryCardColor={accountTheme.entryCardColor()}
              fromAccountSearch={true}
            />
          ))}
        </div>
      )}
    </div>
  );
}
